/**
 * @typedef {import('../types').FiltersStore} FiltersStore
 * @typedef {import('../types').FiltersState} FiltersState
 */

const POSITION_VALUES_KEY = 'props.position.values';
const POSITION_VALUE_KEY = 'props.position.value';
const POSITION_ENABLED_KEY = 'props.position.enabled';
let suppressLegacyToggle = false;
let searchDebounceHandle = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

/**
 * Normalise a raw position value into our canonical uppercase representation.
 * Empty / "ALL" are treated as "clear filters".
 * @param {unknown} value
 * @returns {string}
 */
function normalisePosition(value) {
  const raw = String(value ?? '').trim().toUpperCase();
  if (!raw || raw === 'ALL') {
    return '';
  }
  return raw;
}

/**
 * Coerce incoming position input (Set | Array | string) to a deduped Set.
 * @param {unknown} input
 * @returns {Set<string>}
 */
function toPositionSet(input) {
  const result = new Set();
  if (input instanceof Set) {
    input.forEach((item) => {
      const normalised = normalisePosition(item);
      if (normalised) {
        result.add(normalised);
      }
    });
    return result;
  }

  let iterable = [];
  if (Array.isArray(input)) {
    iterable = input;
  } else if (typeof input === 'string') {
    iterable = input.split(',');
  } else if (input != null) {
    iterable = [input];
  }

  iterable.forEach((item) => {
    const normalised = normalisePosition(item);
    if (normalised) {
      result.add(normalised);
    }
  });

  return result;
}

/**
 * Helper to update URL params. If `reload` is true, navigates to the new URL.
 * @param {string} key
 * @param {string} value
 * @param {{ reload?: boolean }} [options]
 */
function updateQueryParam(key, value, options = {}) {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    if (options.reload) {
      window.location.assign(url.toString());
    } else {
      window.history.replaceState(null, '', url.toString());
    }
  } catch {
    /* ignore URL errors */
  }
}

/**
 * Track multi-filter visibility on rows so filters can combine.
 * @param {HTMLElement} row
 * @param {'position'|'search'} key
 * @param {boolean} isHidden
 */
function setRowHiddenState(row, key, isHidden) {
  if (!(row instanceof HTMLElement)) {
    return;
  }
  row.dataset[`${key}Hidden`] = isHidden ? '1' : '0';
  row.classList.toggle(`${key}-filter-hidden`, isHidden);
  const positionHidden = row.dataset.positionHidden === '1';
  const searchHidden = row.dataset.searchHidden === '1';
  row.hidden = positionHidden || searchHidden;
}

/**
 * Ask the Show All / Show Less toggle helper to recompute its state.
 */
function refreshShowToggle() {
  try {
    if (typeof window === 'undefined' || typeof window.propsShowToggleReady !== 'function') {
      return;
    }
    window.propsShowToggleReady((api) => {
      if (!api || typeof api.apply !== 'function') {
        return;
      }
      const scope = document.querySelector('.props-odds.props-odds-scope');
      if (scope instanceof HTMLElement) {
        api.apply(scope);
      }
    });
  } catch {
    // Swallow errors – filters should never break the table.
  }
}

/**
 * Load previously selected positions from localStorage (legacy behaviour).
 * @returns {string[]}
 */
function loadStoredPositions() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }
    const raw = window.localStorage.getItem(POSITION_VALUES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((value) => normalisePosition(value))
          .filter(Boolean);
      }
    }
    const fallback = window.localStorage.getItem(POSITION_VALUE_KEY);
    if (fallback) {
      return fallback
        .split(',')
        .map((value) => normalisePosition(value))
        .filter(Boolean);
    }
  } catch {
    /* ignore storage errors */
  }
  return [];
}

/**
 * Persist current position filter selection back into localStorage.
 * @param {Set<string>} positions
 */
function persistPositions(positions) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    const values = Array.from(positions);
    if (values.length) {
      window.localStorage.setItem(POSITION_VALUES_KEY, JSON.stringify(values));
      window.localStorage.setItem(POSITION_VALUE_KEY, values.join(','));
      window.localStorage.setItem(POSITION_ENABLED_KEY, '1');
    } else {
      window.localStorage.removeItem(POSITION_VALUES_KEY);
      window.localStorage.setItem(POSITION_VALUE_KEY, '');
      window.localStorage.setItem(POSITION_ENABLED_KEY, '0');
    }
  } catch {
    /* ignore storage errors */
  }
}

/**
 * Apply position filters to comparison table rows by toggling a CSS class.
 * @param {Set<string>} selectedPositions
 */
function applyPositionFilterToTable(selectedPositions) {
  const rows = document.querySelectorAll('.props-comparison-table tbody tr');
  const active = Array.from(selectedPositions);
  const enabled = active.length > 0;

  rows.forEach((row) => {
    if (!(row instanceof HTMLElement)) {
      return;
    }
    if (!enabled) {
      setRowHiddenState(row, 'position', false);
      return;
    }
    const position = String(row.getAttribute('data-position') || '').toUpperCase();
    const matches = active.some((needle) => needle && position.includes(needle));
    setRowHiddenState(row, 'position', !matches);
  });
  refreshShowToggle();
}

/**
 * Apply search filtering to table rows.
 * @param {string} term
 */
function applySearchToTable(term) {
  const rows = document.querySelectorAll('.props-comparison-table tbody tr');
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) {
    rows.forEach((row) => setRowHiddenState(row, 'search', false));
    refreshShowToggle();
    return;
  }
  rows.forEach((row) => {
    if (!(row instanceof HTMLElement)) {
      return;
    }
    const first = (row.getAttribute('data-player-first') || '').toLowerCase();
    const last = (row.getAttribute('data-player-last') || '').toLowerCase();
    const full = (row.getAttribute('data-player-full') || '').toLowerCase();
    const text = row.textContent ? row.textContent.toLowerCase() : '';
    const hit =
      (first && first.includes(needle)) ||
      (last && last.includes(needle)) ||
      (full && full.includes(needle)) ||
      text.includes(needle);
    setRowHiddenState(row, 'search', !hit);
  });
  refreshShowToggle();
}

/**
 * Apply sort selection to the comparison table.
 * @param {string} sortValue
 */
function applySortToTable(sortValue) {
  const table = document.querySelector('.props-comparison-table');
  if (!(table instanceof HTMLElement)) {
    return;
  }
  const tbody = table.querySelector('tbody');
  if (!(tbody instanceof HTMLElement)) {
    return;
  }

  const rows = Array.from(tbody.querySelectorAll('tr'));
  if (!rows.length) {
    return;
  }

  const value = sortValue || 'time';
  if (value === 'player') {
    rows.sort((a, b) => {
      const aKey = String(a.getAttribute('data-player-first') || '').toLowerCase();
      const bKey = String(b.getAttribute('data-player-first') || '').toLowerCase();
      if (aKey === bKey) {
        return 0;
      }
      return aKey < bKey ? -1 : 1;
    });
  } else {
    rows.sort((a, b) => {
      const aAttr = a.getAttribute('data-game-time') || '';
      const bAttr = b.getAttribute('data-game-time') || '';
      const aTs = Number.isNaN(Date.parse(aAttr)) ? Number.MAX_SAFE_INTEGER : Date.parse(aAttr);
      const bTs = Number.isNaN(Date.parse(bAttr)) ? Number.MAX_SAFE_INTEGER : Date.parse(bAttr);
      if (aTs === bTs) {
        const aLabel = String(a.getAttribute('data-matchup') || '').toLowerCase();
        const bLabel = String(b.getAttribute('data-matchup') || '').toLowerCase();
        if (aLabel === bLabel) {
          return 0;
        }
        return aLabel < bLabel ? -1 : 1;
      }
      return aTs - bTs;
    });
  }

  rows.forEach((row) => {
    tbody.appendChild(row);
  });
}

/**
 * Wire up the filters (positions + sort) UI to the shared store.
 * @param {FiltersStore} store
 */
export function registerFiltersPanelModule(store) {
  if (!store || typeof document === 'undefined') {
    return;
  }

  const filtersConfig = store.config?.filters || {};
  const positionLabels = filtersConfig.positionLabels || {};
  const sortOptions = Array.isArray(filtersConfig.sortOptions)
    ? filtersConfig.sortOptions
    : [];
  const sortLabelByValue = new Map(
    sortOptions.map((option) => [option.value, option.label]),
  );
  const defaultSort =
    sortOptions.find((option) => option && option.default)?.value ||
    sortOptions[0]?.value ||
    'time';

  const positionsSelect = document.querySelector('#props-positions-native');
  const positionsLabel = document.querySelector('#props-positions-label');
  const positionsSummary = document.querySelector('[data-props-positions-summary]');
  const positionsToggleBtn = document.querySelector('[data-props-positions-toggle]');
  let positionsInlineBody = document.querySelector('[data-props-positions-body]');
  let positionsInlineList = positionsInlineBody?.querySelector('[data-props-positions-list]') || null;
  const positionsWrapper = positionsToggleBtn?.closest('.props-positions-filter') || null;
  const positionsHiddenInput = document.querySelector('#props-position-select');
  const searchInput = document.querySelector('#props-player-filter');
  const searchClearButtons = Array.from(
    document.querySelectorAll('[data-action="clear-player-search"]'),
  ).map((btn) => {
    btn.hidden = true;
    if (btn instanceof HTMLElement) {
      btn.style.display = 'none';
    }
    return btn;
  });
  function toggleSearchClear(show) {
    searchClearButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) {
        return;
      }
      button.style.display = show ? 'inline-flex' : 'none';
      button.hidden = !show;
    });
  }
  function clearSearch() {
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
    }
    handleSearchChange('');
    updateQueryParam('search', '', { reload: false });
    updateQueryParam('player', '', { reload: false });
    toggleSearchClear(false);
    
    // Call the global filterByPlayer to actually apply the filter reset
    if (typeof window.filterByPlayer === 'function') {
      window.filterByPlayer('');
    }
    
    // Ensure all rows are visible after clearing search
    // Use setTimeout to ensure the DOM and event handlers are ready
    setTimeout(() => {
      const showAllButton = document.querySelector('.props-show-all-button');
      if (showAllButton && showAllButton.getAttribute('aria-expanded') !== 'true') {
        showAllButton.click();
      }
    }, 0);
  }
  const weekSelect = document.querySelector('#props-week-select');
  const dateInput = document.querySelector('#props-date-input');
  const dateSummary = document.querySelector('[data-props-date-summary]');
  const dateTodayButton = document.querySelector('[data-action="weekdate-today"]');
  const dateClearButton = document.querySelector('[data-action="weekdate-clear"]');

  // Sort disabled; keep references null to avoid wiring
  const sortToggle = null;
  const sortMenu = null;
  const sortLabel = null;
  const sortSelect = null;
  let sortDialog = null;

  const lastPositionState = {
    signature: '',
  };
  let currentPositions = toPositionSet(store.getState().positionFilters);
  let suppressPositionNotifications = false;
  const positionSubscribers = new Set();
  let lastSearchValue = store.getState().searchTerm || '';

  // Optional persistence only; rely primarily on URL/bootstrap state
  persistPositions(currentPositions);

  let lastSortValue = store.getState().sortOption || defaultSort;
  if (!lastSortValue && defaultSort) {
    if (store.actions?.setSortOption) {
      store.actions.setSortOption(defaultSort);
    } else if (store.setState) {
      store.setState({ sortOption: defaultSort });
    }
    lastSortValue = defaultSort;
  }
  let isPositionsOverlayOpen = false;

  function buildPositionsInlineOptions() {
    if (!(positionsInlineList instanceof HTMLElement)) {
      return;
    }
    // If server-rendered options exist, leave them as-is to avoid layout shifts.
    if (positionsInlineList.childElementCount > 0) {
      return;
    }
    const options = positionsSelect instanceof HTMLSelectElement
      ? Array.from(positionsSelect.options)
      : [];
    positionsInlineList.innerHTML = '';
    options.forEach((option) => {
      const value = option.value || '';
      const label = option.textContent || option.label || value;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'positions-inline-option';
      button.setAttribute('data-pos', value);
      button.setAttribute('role', 'option');
      button.textContent = label;
      positionsInlineList.appendChild(button);
    });
  }

  function ensurePositionsOverlay() {
    if (positionsInlineBody instanceof HTMLElement && positionsInlineList instanceof HTMLElement) {
      return true;
    }
    if (!(positionsWrapper instanceof HTMLElement)) {
      return false;
    }
    const body = document.createElement('div');
    body.className = 'positions-inline-body';
    body.setAttribute('data-props-positions-body', 'true');
    body.hidden = true;

    const list = document.createElement('div');
    list.className = 'positions-inline-list';
    list.id = 'props-positions-inline-list';
    list.setAttribute('data-props-positions-list', 'true');
    list.setAttribute('role', 'listbox');

    body.appendChild(list);
    positionsWrapper.appendChild(body);
    positionsInlineBody = body;
    positionsInlineList = list;
    buildPositionsInlineOptions();
    return true;
  }

  ensurePositionsOverlay();

  function togglePositionsOverlay(forceOpen) {
    if (!ensurePositionsOverlay() || !(positionsInlineBody instanceof HTMLElement)) {
      if (positionsSelect instanceof HTMLSelectElement) {
        if (typeof positionsSelect.showPicker === 'function') {
          positionsSelect.showPicker();
        } else {
          positionsSelect.focus();
          positionsSelect.click();
        }
      }
      return;
    }
    const next = typeof forceOpen === 'boolean' ? forceOpen : !isPositionsOverlayOpen;
    isPositionsOverlayOpen = next;
    positionsInlineBody.hidden = !next;
    if (positionsWrapper) {
      positionsWrapper.classList.toggle('is-open', next);
    }
    if (positionsToggleBtn) {
      positionsToggleBtn.setAttribute('aria-expanded', next ? 'true' : 'false');
    }
    if (next) {
      document.dispatchEvent(new CustomEvent('props-dropdown-open', { detail: 'positions' }));
      const active = positionsInlineBody.querySelector('.positions-inline-option.is-active');
      const fallback = positionsInlineBody.querySelector('.positions-inline-option');
      if (active instanceof HTMLElement) {
        active.focus({ preventScroll: true });
      } else if (fallback instanceof HTMLElement) {
        fallback.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Update UI affordances for positions with the current selection.
   * @param {Set<string>} positions
   */
  function updatePositionsUI(positions) {
    const values = Array.from(positions);
    buildPositionsInlineOptions();

    if (positionsSelect instanceof HTMLSelectElement) {
      positionsSelect.value = values[0] || '';
    }

    if (positionsHiddenInput instanceof HTMLInputElement) {
      positionsHiddenInput.value = values[0] || '';
    }

    if (positionsLabel) {
      if (!values.length) {
        positionsLabel.textContent = 'Positions';
      } else {
        const readable = values
          .map((value) => positionLabels[value] || value)
          .join(', ');
        positionsLabel.textContent = `Positions (${readable})`;
      }
    }
    if (positionsSummary) {
      if (!values.length) {
        positionsSummary.textContent = 'All';
      } else {
        const readable = values
          .map((value) => positionLabels[value] || value)
          .join(', ');
        positionsSummary.textContent = readable;
      }
    }
    if (positionsInlineList instanceof HTMLElement) {
      const optionButtons = positionsInlineList.querySelectorAll('.positions-inline-option');
      optionButtons.forEach((button) => {
        const raw = String(button.getAttribute('data-pos') || '').trim().toUpperCase();
        const isActive = raw ? positions.has(raw) : !values.length;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    updateQueryParam('positions', values[0] || '', { reload: false });
    applyPositionFilterToTable(positions);
  }

  /**
   * Update search inputs and apply filtering.
   * @param {string} value
   */
  function updateSearchUI(value) {
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = value;
    }
    applySearchToTable(value);
    updateQueryParam('search', value || '', { reload: false });
    toggleSearchClear(!!value);
  }

  /**
   * Update UI + table for sort selection.
   * @param {string} sortValue
   */
  function updateSortUI(sortValue) {
    const value = sortValue || defaultSort;

    if (sortMenu) {
      sortMenu.querySelectorAll('.props-select-option').forEach((option) => {
        const optionValue = option.getAttribute('data-value') || '';
        option.setAttribute('aria-selected', optionValue === value ? 'true' : 'false');
      });
    }

    if (sortSelect instanceof HTMLSelectElement) {
      sortSelect.value = value;
    }

    if (sortLabel) {
      sortLabel.textContent = sortLabelByValue.get(value) || 'Sort';
    }

    applySortToTable(value);
  }

  /**
   * Notify legacy subscribers (if any) with a cloned Set.
   * @param {Set<string>} positions
   */
  function notifyPositionSubscribers(positions) {
    const snapshot = new Set(positions);
    positionSubscribers.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[filters] positions subscriber error', error);
        }
      }
    });
  }

  /**
   * Set the active position as a single-value selection.
   * @param {string} rawValue
   */
  function handlePositionSelect(rawValue) {
    const value = normalisePosition(rawValue);
    if (!value) {
      if (store.actions?.setPositionFilters) {
        store.actions.setPositionFilters([]);
      } else if (store.setState) {
        store.setState({ positionFilters: new Set() });
      }
      return;
    }
    const payload = [value];
    if (store.actions?.setPositionFilters) {
      store.actions.setPositionFilters(payload);
    } else if (store.setState) {
      store.setState({ positionFilters: new Set(payload) });
    }
  }

  /**
   * Update sort selection via the store.
   * @param {string} value
   */
  function handleSortChange(value) {
    const target = value || defaultSort;
    if (!target) {
      return;
    }
    if (store.actions?.setSortOption) {
      store.actions.setSortOption(target);
    } else if (store.setState) {
      store.setState({ sortOption: target });
    }
  }

  /**
   * Update search term via the store.
   * @param {string} value
   */
  function handleSearchChange(value) {
    const next = value || '';
    if (store.actions?.setSearchTerm) {
      store.actions.setSearchTerm(next);
    } else if (store.setState) {
      store.setState({ searchTerm: next });
    }
  }

  /**
   * Handle week change (navigates).
   * @param {string} value
   */
  function handleWeekChange(value) {
    updateQueryParam('date', '', { reload: false });
    updateQueryParam('week', value || '', { reload: true });
  }

  /**
   * Handle date change (navigates).
   * @param {string} value
   */
  function handleDateChange(value) {
    updateQueryParam('week', '', { reload: false });
    updateQueryParam('date', value || '', { reload: true });
    if (dateSummary) {
      dateSummary.textContent = value || '';
    }
    if (dateInput instanceof HTMLInputElement) {
      dateInput.value = value || '';
    }
  }

  if (positionsInlineBody instanceof HTMLElement && !positionsInlineBody.__bound) {
    positionsInlineBody.addEventListener('click', (event) => {
      const button = event.target instanceof HTMLElement
        ? event.target.closest('.positions-inline-option')
        : null;
      if (!(button instanceof HTMLElement)) {
        return;
      }
      const value = button.getAttribute('data-pos') || '';
      suppressLegacyToggle = true;
      try {
        handlePositionSelect(value);
      } finally {
        suppressLegacyToggle = false;
      }
      togglePositionsOverlay(false);
    });
    positionsInlineBody.__bound = true;
  }

  if (positionsSelect instanceof HTMLSelectElement) {
    positionsSelect.addEventListener('change', (event) => {
      const value = event.currentTarget instanceof HTMLSelectElement
        ? event.currentTarget.value
        : '';
      suppressLegacyToggle = true;
      try {
        handlePositionSelect(value);
      } finally {
        suppressLegacyToggle = false;
      }
      togglePositionsOverlay(false);
    });
  }

  const debouncedSearch = (value) => {
    if (searchDebounceHandle) {
      clearTimeout(searchDebounceHandle);
    }
    searchDebounceHandle = setTimeout(() => {
      handleSearchChange(value);
    }, 150);
  };

  if (searchInput instanceof HTMLInputElement) {
    searchInput.addEventListener('input', (event) => {
      const value = event.currentTarget instanceof HTMLInputElement
        ? event.currentTarget.value
        : '';
      debouncedSearch(value);
    });
  }

  searchClearButtons.forEach((button) => {
    if (!(button instanceof HTMLElement)) {
      return;
    }
    button.addEventListener('click', () => {
      clearSearch();
    });
  });

  if (positionsToggleBtn instanceof HTMLElement) {
    positionsToggleBtn.addEventListener('click', (event) => {
      event.preventDefault();
      togglePositionsOverlay();
    });
  }

  document.addEventListener('click', (event) => {
    if (!isPositionsOverlayOpen || !(positionsInlineBody instanceof HTMLElement)) {
      return;
    }
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (positionsToggleBtn?.contains(target) || positionsInlineBody.contains(target)) {
      return;
    }
    togglePositionsOverlay(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isPositionsOverlayOpen) {
      togglePositionsOverlay(false);
      if (positionsToggleBtn instanceof HTMLElement) {
        positionsToggleBtn.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('props-dropdown-open', (event) => {
    if (event?.detail === 'positions') {
      return;
    }
    togglePositionsOverlay(false);
  });

  if (weekSelect instanceof HTMLSelectElement) {
    weekSelect.addEventListener('change', (event) => {
      const value = event.currentTarget instanceof HTMLSelectElement
        ? event.currentTarget.value
        : '';
      handleWeekChange(value);
    });
  }

  if (dateInput instanceof HTMLInputElement) {
    dateInput.addEventListener('change', (event) => {
      const value = event.currentTarget instanceof HTMLInputElement
        ? event.currentTarget.value
        : '';
      handleDateChange(value);
    });
  }

  if (dateTodayButton instanceof HTMLElement && dateInput instanceof HTMLInputElement) {
    dateTodayButton.addEventListener('click', (event) => {
      event.preventDefault();
      const today = dateInput.getAttribute('data-today') || '';
      dateInput.value = today;
      handleDateChange(today);
    });
  }

  if (dateClearButton instanceof HTMLElement && dateInput instanceof HTMLInputElement) {
    dateClearButton.addEventListener('click', (event) => {
      event.preventDefault();
      dateInput.value = '';
      handleDateChange('');
    });
  }

  if (sortMenu) {
    sortMenu.addEventListener('click', (event) => {
      const option = event.target instanceof HTMLElement
        ? event.target.closest('.props-select-option')
        : null;
      if (!(option instanceof HTMLElement)) {
        return;
      }
      event.preventDefault();
      const value = option.getAttribute('data-value') || '';
      handleSortChange(value);
      sortDialog?.close();
    });
  }

  if (sortSelect instanceof HTMLSelectElement) {
    sortSelect.addEventListener('change', (event) => {
      const value = event.currentTarget instanceof HTMLSelectElement
        ? event.currentTarget.value
        : '';
      handleSortChange(value);
    });
  }

  const unsubscribe = store.subscribe((state) => {
    let positionSet = toPositionSet(state.positionFilters);
    if (positionSet.size > 1) {
      const [first] = Array.from(positionSet);
      positionSet = new Set(first ? [first] : []);
    }
    const signature = Array.from(positionSet).sort().join(',');
    if (signature !== lastPositionState.signature) {
      lastPositionState.signature = signature;
      currentPositions = positionSet;
      persistPositions(positionSet);
      updatePositionsUI(positionSet);
      if (!suppressPositionNotifications) {
        notifyPositionSubscribers(positionSet);
      } else {
        suppressPositionNotifications = false;
      }
    }

    const sortValue = state.sortOption || defaultSort;
    if (sortValue && sortValue !== lastSortValue) {
      lastSortValue = sortValue;
      updateSortUI(sortValue);
    }

    const searchValue = state.searchTerm || '';
    if (searchValue !== lastSearchValue) {
      lastSearchValue = searchValue;
      updateSearchUI(searchValue);
    }
  });

  updatePositionsUI(currentPositions);
  updateSortUI(lastSortValue);
  updateSearchUI(lastSearchValue);

  if (typeof window !== 'undefined') {
    const legacyApi = {
      get: () => new Set(currentPositions),
      list: () => Array.from(currentPositions),
      set: (values, options) => {
        suppressPositionNotifications = !!(options && options.silent);
        const next = Array.from(toPositionSet(values));
        if (store.actions?.setPositionFilters) {
          store.actions.setPositionFilters(next);
        } else if (store.setState) {
          store.setState({ positionFilters: new Set(next) });
        }
      },
      toggle: (value) => {
        if (suppressLegacyToggle) {
          return;
        }
        handlePositionSelect(value);
      },
      clear: (options) => {
        suppressPositionNotifications = !!(options && options.silent);
        if (store.actions?.setPositionFilters) {
          store.actions.setPositionFilters([]);
        } else if (store.setState) {
          store.setState({ positionFilters: new Set() });
        }
      },
      isEnabled: () => currentPositions.size > 0,
      subscribe: (listener, options) => {
        if (typeof listener !== 'function') {
          return () => {};
        }
        positionSubscribers.add(listener);
        if (!options || options.immediate !== false) {
          try {
            listener(new Set(currentPositions));
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
              console.error('[filters] positions immediate subscriber error', error);
            }
          }
        }
        return () => {
          positionSubscribers.delete(listener);
        };
      },
      loadFromStorage: () => loadStoredPositions(),
      applyFilter: (selected, rows) => {
        applyPositionFilterToTable(toPositionSet(selected));
        if (rows && typeof rows.forEach === 'function') {
          // no-op: table rows are handled globally above.
        }
      },
      __v2: true,
    };

    if (!window.propsPositionsState || !window.propsPositionsState.__v2) {
      window.propsPositionsState = legacyApi;
    } else {
      // Merge with existing implementation to preserve references.
      window.propsPositionsState = Object.assign(window.propsPositionsState, legacyApi);
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('unload', () => {
      unsubscribe();
    });
  }
}
